import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  AccountDto,
  CatalogItemDto,
  CompanySettings,
  CreateSalesInvoiceRequest,
  CustomerDto,
  SalesInvoiceDto,
  SalesInvoiceSummary,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError, UnauthorizedError } from './api';
import InvoiceAdjustments from './InvoiceAdjustments';
import { invoiceDocument, PrintPreview } from './PrintDocument';
import { formatMoney, parseAmount } from './money';
import Loading from './Loading';
import EmptyState from './EmptyState';
import { useRevealOnOpen } from './reveal';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  accounts: readonly AccountDto[];
  canIssue: boolean;
  /** الإلغاء يعكس قيدًا منشورًا — `journal.manual` لا `journal.post`. */
  canCancel: boolean;
  onSessionLost: () => void;
}

interface DraftLine {
  id: number;
  itemId: string;
  description: string;
  quantity: string;
  unitPrice: string;
  revenueAccountCode: string;
}

let nextLineId = 1;
const emptyLine = (): DraftLine => ({
  id: nextLineId++,
  itemId: '',
  description: '',
  quantity: '1',
  unitPrice: '',
  revenueAccountCode: '',
});

function today(): string {
  const now = new Date();
  return (
    now.getFullYear() +
    '-' +
    String(now.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(now.getDate()).padStart(2, '0')
  );
}

const ISSUE_LABELS: Record<string, string> = {
  INVOICE_REVENUE_ACCOUNT: 'حساب الإيراد يجب أن يكون حساب إيراد طرفيًا',
  INVOICE_LINE_QUANTITY: 'الكمية يجب أن تكون موجبة',
  INVOICE_LINE_PRICE: 'السعر لا يكون سالبًا',
  INVOICE_ZERO_TOTAL: 'إجمالى الفاتورة يجب أن يكون موجبًا',
  DUE_BEFORE_ISSUE: 'تاريخ الاستحقاق قبل تاريخ الإصدار',
  CUSTOMER_NOT_FOUND: 'العميل غير موجود',
  CUSTOMER_INACTIVE: 'العميل موقوف',
  TAX_RULE_NOT_APPROVED: 'القاعدة الضريبية غير معتمدة',
  ITEM_NOT_FOUND: 'الصنف غير موجود أو موقوف',
  STOCK_INSUFFICIENT: 'رصيد الصنف لا يكفى',
  STOCK_BACKDATED: 'تاريخ الفاتورة قبل آخر حركة لهذا الصنف',
  PERIOD_CLOSED: 'الفترة المالية مقفلة',
  DATE_OUTSIDE_PERIOD: 'التاريخ خارج الفترة المالية',
};

/** صافى السطر للعرض فقط — الخادم يعيد التسعير بنفس القاعدة وهو المرجع. */
function previewNet(line: DraftLine): Money | null {
  const quantity = parseAmount(line.quantity);
  const price = parseAmount(line.unitPrice);
  if (quantity === null || price === null) return null;
  return Money.of(quantity).mul(price).round(2, 'half_up');
}

/**
 * فواتير الخدمات: إصدار وقائمة وتفاصيل. الفاتورة تُرحَّل لحظة إصدارها ولا تُعدَّل،
 * والضريبة لا تظهر هنا حتى تُعتمد قواعدها (BLOCKED-TAX).
 */
export default function SalesInvoices({
  companyId,
  currency,
  accounts,
  canIssue,
  canCancel,
  onSessionLost,
}: Props) {
  const [items, setItems] = useState<CatalogItemDto[]>([]);
  const [salesSettings, setSalesSettings] = useState<CompanySettings['sales'] | null>(null);
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  // كل العملاء لا النشطين فقط: فاتورة قديمة لعميل موقوف تُطبع ببياناته أيضًا
  const [allCustomers, setAllCustomers] = useState<CustomerDto[]>([]);
  const [printing, setPrinting] = useState(false);
  const [invoices, setInvoices] = useState<SalesInvoiceSummary[] | null>(null);
  const [selected, setSelected] = useState<SalesInvoiceDto | null>(null);
  const detailRef = useRevealOnOpen<HTMLDivElement>(selected?.id);
  const [customerId, setCustomerId] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<DraftLine[]>(() => [emptyLine()]);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<{ code: string; message: string; lineIndex?: number }[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [issued, setIssued] = useState<SalesInvoiceDto | null>(null);

  const revenueAccounts = useMemo(
    () => accounts.filter((account) => account.accountClass === 'revenue' && account.isPostable),
    [accounts],
  );

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        setIssues(caught.problem.issues ?? []);
        setFailure(
          caught.problem.issues?.length ? null : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  const load = useCallback(() => {
    Promise.all([
      api.customers(companyId),
      api.salesInvoices(companyId),
      api.catalogItems(companyId, true),
      api.companySettings(companyId),
    ])
      .then(([customerList, invoiceList, itemList, settings]) => {
        setAllCustomers(customerList);
        setCustomers(customerList.filter((customer) => customer.isActive));
        setInvoices(invoiceList);
        setItems(itemList);
        setSalesSettings(settings.settings.sales);
      })
      .catch(handleError);
  }, [companyId, handleError]);

  useEffect(load, [load]);

  function touched(): void {
    setIdempotencyKey(crypto.randomUUID());
    setIssues([]);
    setFailure(null);
    setIssued(null);
  }

  /** الصنف يملأ البيان والسعر والحساب، ويبقى كل منها قابلاً للتعديل بعده. */
  function pickItem(id: number, itemId: string): void {
    const item = items.find((candidate) => candidate.id === itemId);
    updateLine(
      id,
      item
        ? {
            itemId,
            description: item.nameAr,
            unitPrice: Money.of(item.defaultPrice).normalize().toString(),
            revenueAccountCode: item.revenueAccountCode ?? '',
          }
        : { itemId: '' },
    );
  }

  function updateLine(id: number, patch: Partial<DraftLine>): void {
    setLines((current) => current.map((line) => (line.id === id ? { ...line, ...patch } : line)));
    touched();
  }

  const nets = lines.map(previewNet);
  const total = nets.every((net) => net !== null) ? Money.sum(nets as Money[]) : null;
  const ready =
    customerId !== '' &&
    lines.every((line) => line.description.trim() !== '' && previewNet(line) !== null) &&
    total !== null &&
    total.isPositive();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready) return;
    setSubmitting(true);
    setIssues([]);
    setFailure(null);
    const body: CreateSalesInvoiceRequest = {
      customerId,
      issueDate,
      ...(memo.trim() ? { memo: memo.trim() } : {}),
      lines: lines.map((line) => ({
        ...(line.itemId ? { itemId: line.itemId } : {}),
        description: line.description.trim(),
        quantity: parseAmount(line.quantity) ?? line.quantity,
        unitPrice: parseAmount(line.unitPrice) ?? line.unitPrice,
        ...(line.revenueAccountCode ? { revenueAccountCode: line.revenueAccountCode } : {}),
      })),
    };
    try {
      const invoice = await api.createSalesInvoice(companyId, idempotencyKey, body);
      setIssued(invoice);
      setLines([emptyLine()]);
      setMemo('');
      setIdempotencyKey(crypto.randomUUID());
      load();
    } catch (caught) {
      handleError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  function open(invoiceId: string): void {
    api.salesInvoice(companyId, invoiceId).then(setSelected).catch(handleError);
  }

  const generalIssues = issues.filter((issue) => issue.lineIndex === undefined);

  return (
    <>
      {canIssue && (
        <form className="panel" onSubmit={(event) => void submit(event)} noValidate>
          <h2>فاتورة خدمة</h2>
          <div className="form-row">
            <label className="grow">
              العميل
              <select
                value={customerId}
                onChange={(event) => {
                  setCustomerId(event.target.value);
                  touched();
                }}
              >
                <option value="">— اختر عميلاً —</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.code} — {customer.nameAr} ({customer.paymentTermsDays} يوم)
                  </option>
                ))}
              </select>
              <Hint>الرقم بين القوسين مدة سداده؛ منها يُحسب تاريخ استحقاق الفاتورة تلقائيًا.</Hint>
            </label>
            <label>
              تاريخ الإصدار
              <input
                type="date"
                value={issueDate}
                onChange={(event) => {
                  setIssueDate(event.target.value);
                  touched();
                }}
              />
              <Hint>يحدد الشهر الذى تُحسب فيه الفاتورة فى التقارير.</Hint>
            </label>
            <label className="grow">
              بيان
              <input
                value={memo}
                maxLength={500}
                onChange={(event) => {
                  setMemo(event.target.value);
                  touched();
                }}
              />
            </label>
          </div>
          {customers.length === 0 && (
            <p className="sub">لا عملاء بعد — أضف عميلاً من شاشة العملاء أولاً.</p>
          )}

          <div className="table-scroll">
            <table className="lines-form">
              <thead>
                <tr>
                  {items.length > 0 && <th>الصنف</th>}
                  <th>الوصف</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                  <th>حساب الإيراد</th>
                  <th>الصافى</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const lineIssues = issues.filter((issue) => issue.lineIndex === index);
                  const net = nets[index];
                  return (
                    <tr key={line.id}>
                      {items.length > 0 && (
                        <td data-label="الصنف">
                          <select
                            value={line.itemId}
                            onChange={(event) => pickItem(line.id, event.target.value)}
                          >
                            <option value="">— بلا صنف —</option>
                            {items.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.nameAr} ({item.unit})
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td data-label="الوصف">
                        <input
                          value={line.description}
                          maxLength={500}
                          onChange={(event) =>
                            updateLine(line.id, { description: event.target.value })
                          }
                        />
                        {lineIssues.map((issue, i) => (
                          <p key={i} className="error">
                            {ISSUE_LABELS[issue.code] ?? issue.message}
                          </p>
                        ))}
                      </td>
                      <td data-label="الكمية">
                        <input
                          inputMode="decimal"
                          value={line.quantity}
                          onChange={(event) =>
                            updateLine(line.id, { quantity: event.target.value })
                          }
                        />
                      </td>
                      <td data-label="السعر">
                        <input
                          inputMode="decimal"
                          value={line.unitPrice}
                          onChange={(event) =>
                            updateLine(line.id, { unitPrice: event.target.value })
                          }
                        />
                      </td>
                      <td data-label="حساب الإيراد">
                        <select
                          value={line.revenueAccountCode}
                          onChange={(event) =>
                            updateLine(line.id, { revenueAccountCode: event.target.value })
                          }
                        >
                          <option value="">الافتراضى (إيرادات الخدمات)</option>
                          {revenueAccounts.map((account) => (
                            <option key={account.code} value={account.code}>
                              {account.code} — {account.nameAr}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td data-label="الصافى" className="num-cell">
                        {net ? formatMoney(net) : '—'}
                      </td>
                      <td>
                        {lines.length > 1 && (
                          <button
                            type="button"
                            className="link"
                            onClick={() => {
                              setLines((current) => current.filter((item) => item.id !== line.id));
                              touched();
                            }}
                          >
                            حذف
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td colSpan={items.length > 0 ? 5 : 4}>
                    الإجمالى (بلا ضريبة حتى تُعتمد قواعدها)
                  </td>
                  <td className="num-cell total">{total ? formatMoney(total) : '—'}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {generalIssues.map((issue, i) => (
            <p key={i} className="error">
              {ISSUE_LABELS[issue.code] ?? issue.message}
            </p>
          ))}
          {failure && <p className="error">{failure}</p>}
          {issued && (
            <p className="sub">
              صدرت الفاتورة {issued.number} بإجمالى {formatMoney(issued.total)} {currency} — قيدها{' '}
              {issued.journalNumber}، والاستحقاق {issued.dueDate}.
            </p>
          )}

          <div className="form-actions sticky">
            <button type="submit" className="primary" disabled={submitting || !ready}>
              {submitting ? 'جارى الإصدار…' : 'إصدار الفاتورة'}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setLines((current) => [...current, emptyLine()]);
                touched();
              }}
            >
              سطر جديد
            </button>
            <span className="label">
              الفاتورة تُرحَّل لحظة إصدارها ولا تُعدَّل — التصحيح بإشعار دائن.
            </span>
          </div>
        </form>
      )}

      <div className="panel">
        <h2>الفواتير</h2>
        {invoices === null && !failure && <Loading />}
        {invoices?.length === 0 && (
          <EmptyState icon="invoice" title="لا فواتير بعد">
            كل فاتورة تُصدرها تُرحَّل فورًا إلى دفاترك وتظهر هنا مع المتبقى منها.
          </EmptyState>
        )}
        {invoices && invoices.length > 0 && (
          <div className="table-scroll">
            <table className="stack-mobile">
              <thead>
                <tr>
                  <th>الرقم</th>
                  <th>العميل</th>
                  <th>الإصدار</th>
                  <th>الاستحقاق</th>
                  <th>الإجمالى</th>
                  <th>المتبقى</th>
                  <th>القيد</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="clickable"
                    tabIndex={0}
                    onClick={() => open(invoice.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') open(invoice.id);
                    }}
                  >
                    <td data-label="الرقم" className="mono">
                      {invoice.number}
                    </td>
                    <td data-label="العميل">{invoice.customerName}</td>
                    <td data-label="الإصدار">{invoice.issueDate}</td>
                    <td data-label="الاستحقاق">{invoice.dueDate}</td>
                    <td data-label="الإجمالى" className="num-cell">
                      {formatMoney(invoice.total)}
                    </td>
                    <td data-label="المتبقى" className="num-cell">
                      {invoice.status === 'cancelled' ? (
                        <span className="pill bad">ملغاة</span>
                      ) : (
                        formatMoney(invoice.openBalance)
                      )}
                    </td>
                    <td data-label="القيد" className="mono">
                      {invoice.journalNumber}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="panel" ref={detailRef}>
          <div className="panel-head">
            <h2>
              فاتورة {selected.number} — {selected.customerName}
              {selected.status === 'cancelled' && <span className="pill bad">ملغاة</span>}
            </h2>
            <button type="button" className="ghost no-print" onClick={() => setPrinting(true)}>
              طباعة / PDF
            </button>
          </div>
          <p className="sub">
            الإصدار {selected.issueDate} • الاستحقاق {selected.dueDate} • القيد{' '}
            {selected.journalNumber}
            {selected.memo ? ' • ' + selected.memo : ''}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>الوصف</th>
                  <th>الكمية</th>
                  <th>السعر</th>
                  <th>حساب الإيراد</th>
                  <th>الصافى</th>
                </tr>
              </thead>
              <tbody>
                {selected.lines.map((line) => (
                  <tr key={line.lineNumber}>
                    <td>{line.lineNumber}</td>
                    <td>{line.description}</td>
                    <td className="num-cell">{Money.of(line.quantity).normalize().toString()}</td>
                    <td className="num-cell">{formatMoney(line.unitPrice)}</td>
                    <td className="mono">{line.revenueAccountCode}</td>
                    <td className="num-cell">{formatMoney(line.net)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="closing">
                  <td colSpan={5}>الإجمالى ({selected.currency})</td>
                  <td className="num-cell total">{formatMoney(selected.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <InvoiceAdjustments
            key={selected.id}
            companyId={companyId}
            currency={currency}
            invoice={selected}
            customer={allCustomers.find((customer) => customer.id === selected.customerId)}
            settings={salesSettings}
            canIssueNotes={canIssue}
            canCancel={canCancel}
            onChanged={(invoice) => {
              setSelected(invoice);
              load();
            }}
            onSessionLost={onSessionLost}
          />
        </div>
      )}

      {selected && printing && (
        <PrintPreview
          companyId={companyId}
          doc={invoiceDocument(
            selected,
            allCustomers.find((customer) => customer.id === selected.customerId),
          )}
          onClose={() => setPrinting(false)}
          onSessionLost={onSessionLost}
        />
      )}
    </>
  );
}
